/**
 * Supersets (`D246`) — the app's copy of `@repo/training`'s rule, for the
 * outbox's optimistic drawing, the editor's link between two rows and the
 * workout's rounds.
 *
 * A superset is a MAXIMAL run of consecutive lines of one day sharing a
 * `supersetId`, at least two long; any other line stands on its own, whatever
 * key it carries. The server sends the run's key on a plan read, but a
 * one-line answer (a swap, an undo) carries the stored key, so nothing here
 * trusts the field alone: every reader goes through {@link supersetKeys}.
 * Pure and idempotent (`T-221`). `__tests__/superset.test.ts` runs the domain
 * test's table.
 */
import { MAX_SUPERSET_SIZE } from "@repo/contract/training";
/** The fewest lines a superset holds. */
export const MIN_SUPERSET_SIZE = 2;
export { MAX_SUPERSET_SIZE } from "@repo/contract/training";
/**
 * The key each line of ONE day belongs to, in the day's order: its key when
 * it stands in a run of at least two equal keys, else `null`.
 */
export function supersetKeys(keys) {
    const out = keys.map(() => null);
    let start = 0;
    while (start < keys.length) {
        const key = keys[start] ?? null;
        let end = start + 1;
        while (end < keys.length && key !== null && keys[end] === key) {
            end += 1;
        }
        if (key !== null && end - start >= MIN_SUPERSET_SIZE) {
            for (let at = start; at < end; at += 1) {
                out[at] = key;
            }
        }
        start = end;
    }
    return out;
}
/** The longest rest any member prescribes; `null` when none does. */
export function roundRestSeconds(rests) {
    let longest = null;
    for (const rest of rests) {
        if (rest !== null && (longest === null || rest > longest)) {
            longest = rest;
        }
    }
    return longest;
}
function keysOf(items) {
    return supersetKeys(items.map((item) => item.supersetId ?? null));
}
/**
 * The positions of the superset the line at `index` stands in, in order — or
 * `null` for a line on its own. `items` is one day, in order.
 */
export function supersetMembersAt(items, index) {
    const keys = keysOf(items);
    const key = keys[index] ?? null;
    if (key === null) {
        return null;
    }
    let start = index;
    while (start > 0 && keys[start - 1] === key) {
        start -= 1;
    }
    let end = index + 1;
    while (end < keys.length && keys[end] === key) {
        end += 1;
    }
    return Array.from({ length: end - start }, (_, offset) => start + offset);
}
/** Whether the lines at `index` and `index + 1` are in one superset. */
export function linkedToNext(items, index) {
    const keys = keysOf(items);
    const key = keys[index] ?? null;
    return key !== null && keys[index + 1] === key;
}
function idsAt(items, positions) {
    return positions.flatMap((at) => {
        const id = items[at]?.itemId;
        return id === undefined ? [] : [id];
    });
}
/**
 * "Juntar" between the lines at `index` and `index + 1`: both, with the
 * groups either already stands in, as ONE superset. `null` when the result
 * would hold more than {@link MAX_SUPERSET_SIZE} lines, or there is no next
 * line, or the two are already linked.
 */
export function linkWithNext(items, index) {
    if (index < 0 || index + 1 >= items.length || linkedToNext(items, index)) {
        return null;
    }
    const here = supersetMembersAt(items, index) ?? [index];
    const next = supersetMembersAt(items, index + 1) ?? [index + 1];
    const positions = [...here, ...next];
    if (positions.length > MAX_SUPERSET_SIZE) {
        return null;
    }
    return { grouped: true, itemIds: idsAt(items, positions) };
}
/**
 * "Separar" between the lines at `index` and `index + 1`: the group splits
 * there. The lines after the cut are regrouped when two or more remain —
 * which leaves the lines before it under their old key, a group of their own
 * when they are two or more — or taken out when one remains. ONE write either
 * way. `null` when the two are not linked.
 */
export function unlinkFromNext(items, index) {
    if (!linkedToNext(items, index)) {
        return null;
    }
    const members = supersetMembersAt(items, index) ?? [];
    const after = members.filter((at) => at > index);
    return after.length >= MIN_SUPERSET_SIZE
        ? { grouped: true, itemIds: idsAt(items, after) }
        : { grouped: false, itemIds: idsAt(items, after) };
}
/**
 * A superset write drawn onto one day's lines, in order, as the server will
 * store it (`TrainingPlan.setSuperset`): grouped lines take the first one's
 * id as their key, a line elsewhere that kept that key loses it, and every
 * key no run of two shares is cleared. A group the server would refuse — not
 * next to each other, too many — draws nothing. The SAME array when nothing
 * changes, so a re-applied patch costs no re-render (`T-221`).
 */
export function withSuperset(items, change) {
    const named = new Set(change.itemIds);
    const positions = items.flatMap((item, at) => named.has(item.itemId) ? [at] : []);
    let stored = items.map((item) => item.supersetId ?? null);
    if (change.grouped) {
        const [start] = positions;
        if (start === undefined ||
            positions.length !== change.itemIds.length ||
            positions.length < MIN_SUPERSET_SIZE ||
            positions.length > MAX_SUPERSET_SIZE ||
            positions.some((at, offset) => at !== start + offset)) {
            return items;
        }
        const key = items[start]?.itemId ?? null;
        stored = stored.map((current, at) => {
            if (named.has(items[at]?.itemId ?? "")) {
                return key;
            }
            return current === key ? null : current;
        });
    }
    else {
        stored = stored.map((current, at) => named.has(items[at]?.itemId ?? "") ? null : current);
    }
    const settled = supersetKeys(stored);
    let changed = false;
    const next = items.map((item, at) => {
        const key = settled[at] ?? null;
        if (key === (item.supersetId ?? null)) {
            return item;
        }
        changed = true;
        return { ...item, supersetId: key };
    });
    return changed ? next : items;
}
//# sourceMappingURL=superset.js.map